/**
 * Whether Texas licenses a trade, and what that means for a reader.
 *
 * Isomorphic and category-neutral on purpose. The dashboard form, the public
 * block and the validation all read these constants, so adding a category is a
 * database row rather than a code change, and no rendered string can be
 * specific to roofing or any other vertical.
 *
 * The reason this exists at all: for the nine trades Texas does not license,
 * "there is no licence to check" is the single most useful and least known
 * fact a homeowner can have. It is also the one thing a lead-generation
 * directory will never print, because its advertisers are the unlicensed ones.
 */

export const LICENSING_STATUSES = [
	{
		value: 'licensed',
		label: 'Licensed by the state',
		/** Shown on the public page as the heading for this variant. */
		heading: 'This trade is licensed in Texas',
		description:
			'A state board issues and can revoke the licence, and publishes disciplinary history.',
	},
	{
		value: 'unlicensed',
		label: 'Not licensed by the state',
		heading: 'Texas does not license this trade',
		description:
			'Anyone may legally do this work. No state board vets it, and there is no licence to look up.',
	},
	{
		value: 'conditional',
		label: 'Licensed for part of the work',
		heading: 'Some of this work requires a state licence',
		description:
			'Parts of the trade are regulated and parts are not, so a licence answers only part of the question.',
	},
] as const

export type LicensingStatus = (typeof LICENSING_STATUSES)[number]['value']

export const LICENSING_STATUS_VALUES = LICENSING_STATUSES.map((s) => s.value)

export function isLicensingStatus(value: unknown): value is LicensingStatus {
	return (
		typeof value === 'string' &&
		(LICENSING_STATUS_VALUES as readonly string[]).includes(value)
	)
}

export function licensingStatusMeta(status: LicensingStatus) {
	return LICENSING_STATUSES.find((s) => s.value === status)!
}

/** What a category carries, as stored. */
export interface CategoryLicensing {
	licensingStatus: string | null
	licensingBoard: string | null
	licensingBoardUrl: string | null
	licensingVerifyUrl: string | null
	licensingNote: string | null
}

/**
 * Is there enough here to publish?
 *
 * A status alone is publishable — "Texas does not license this trade" is a
 * complete and useful statement on its own. A licensed or conditional trade
 * without a board name is not: naming the regulator is the whole value, and a
 * vague claim that "a licence is required" sends a reader nowhere.
 *
 * Returning false renders nothing rather than a partial claim, which is what
 * keeps an unresearched category from publishing a regulatory assertion.
 */
export function hasPublishableLicensing(
	category: CategoryLicensing,
): category is CategoryLicensing & { licensingStatus: LicensingStatus } {
	if (!isLicensingStatus(category.licensingStatus)) return false
	if (category.licensingStatus === 'unlicensed') return true
	return Boolean(category.licensingBoard?.trim())
}
